import CalendarEntry from 'owa-service/lib/contract/CalendarEntry';
import CalendarFolder from 'owa-service/lib/contract/CalendarFolder';
import CalendarFoldersProcessedResponse from '../store/schema/CalendarFoldersProcessedResponse';
import CalendarGroup from 'owa-service/lib/contract/CalendarGroup';
import CalendarGroupType from 'owa-service/lib/contract/CalendarGroupType';
import getCalculatedFolderId from 'mini-event-to-calendar-association/lib/utils/getCalculatedFolderId';
import getCalendarColorScheme from 'mini-event-to-calendar-association/lib/utils/calendarColorSchemeManager';
import GetCalendarFoldersResponse from 'owa-service/lib/contract/GetCalendarFoldersResponse';
import LinkedCalendarEntry from 'owa-service/lib/contract/LinkedCalendarEntry';
import LocalCacheForRemoteCalendarEntry from 'owa-service/lib/contract/LocalCacheForRemoteCalendarEntry';
import LocalCalendarEntry from 'owa-service/lib/contract/LocalCalendarEntry';
import LocalCalendarGroup from '../store/schema/CalendarGroup';
import { Calendar } from 'mini-event-to-calendar-association/lib/schema/Calendar';
import { CalendarId } from 'mini-event-to-calendar-association/lib/schema/CalendarId';
import { createAppContext } from 'mini-app-context';
import { getFilteredUserConfiguration } from 'mini-settings/lib/selectors/getFilteredUserConfiguration';
import { getUserMailboxInfo } from 'owa-client-ids';
import { OnlineMeetingProviderType } from 'mini-calendar-event-types';

export default function processCalendarFoldersResponse(
    response: GetCalendarFoldersResponse
): CalendarFoldersProcessedResponse {
    const { CalendarGroups, CalendarFolders } = response;

    const calendarFolderLookup: { [key: string]: CalendarFolder } = {};
    CalendarFolders.forEach(folder => (calendarFolderLookup[folder.FolderId.Id] = folder));

    const resultGroups = [];
    let sortedResultCalendars: Calendar[] = [];
    const calendarIds: Set<string> = new Set<string>();
    let defaultCalendar: Calendar = null;

    const folderIdMapping: { [key: string]: CalendarId } = {};
    const userMailboxInfo = getUserMailboxInfo();

    const ctx = createAppContext();

    CalendarGroups.forEach((group: CalendarGroup) => {
        let groupHasDefaultCalendar = false;
        let resultCalendars: Calendar[] = [];

        // Create calendar group object
        const calendarGroup: LocalCalendarGroup = {
            id: { id: group.ItemId.Id, mailboxInfo: userMailboxInfo },
            groupId: group.GroupId,
            displayName: group.GroupName,
            groupType: group.GroupType.valueOf() as CalendarGroupType,
        };
        resultGroups.push(calendarGroup);

        group.Calendars.forEach(
            (
                calendarEntry: CalendarEntry &
                    LocalCacheForRemoteCalendarEntry &
                    LinkedCalendarEntry &
                    LocalCalendarEntry
            ) => {
                const entryId = calendarEntry.ItemId.Id;
                if (calendarIds.has(entryId)) {
                    ctx?.signal('DuplicateCalendar', { calendarId: entryId });
                    return; // Avoid processing the duplicate calendar
                }
                calendarIds.add(entryId);
                const folderId = getCalculatedFolderId(calendarEntry);
                const calcFolderId = folderId && folderId.Id;

                const currentFolder: CalendarFolder = calendarFolderLookup[calcFolderId];

                const calendar: Calendar = {
                    type: calendarEntry.__type,
                    id: { id: entryId, mailboxInfo: userMailboxInfo },
                    calculatedFolderId: calcFolderId,
                    displayName: calendarEntry.CalendarName,
                    colorScheme: getCalendarColorScheme(calendarEntry),
                    isSelected: false,
                    remoteCategories: calendarEntry.RemoteCategories,
                    CalendarFolderType: calendarEntry.CalendarFolderType,
                    SharedFolderId: calendarEntry.SharedFolderId,
                    isGroupMailboxCalendar: calendarEntry.IsGroupMailboxCalendar,
                    isGeneralScheduleCalendar: calendarEntry.IsGeneralScheduleCalendar,
                    isFolderIDValid: !!currentFolder,
                    SharedOwnerEmailAddress:
                        calendarEntry.SharedOwnerEmailAddress || calendarEntry.OwnerEmailAddress,
                    isPeopleCalendar:
                        calendarGroup.groupType === CalendarGroupType.PeoplesCalendars,
                    isLocalCalendar: isLocalCalendar(calendarEntry),
                    isReadOnly: calendarEntry.IsReadOnly,
                };

                // Check if the current calendar is a default calendar
                if ((calendarEntry as LocalCalendarEntry).IsDefaultCalendar) {
                    defaultCalendar = calendar;
                    groupHasDefaultCalendar = true;
                }

                // Writability
                calendar.isWritable = isWritableCalendar(currentFolder, calendarEntry.IsReadOnly);

                // Online meeting info
                calendar.AllowedOnlineMeetingProviders = [
                    ...(calendarEntry?.AllowedOnlineMeetingProviders || []),
                    ...(currentFolder?.AllowedOnlineMeetingProviders || []),
                ] as OnlineMeetingProviderType[];
                calendar.DefaultOnlineMeetingProvider = (calendarEntry?.DefaultOnlineMeetingProvider ||
                    currentFolder?.DefaultOnlineMeetingProvider) as OnlineMeetingProviderType;

                resultCalendars.push(calendar);

                !folderIdMapping[calcFolderId] &&
                    (folderIdMapping[calcFolderId] = {
                        id: calendarEntry.ItemId.Id,
                        mailboxInfo: userMailboxInfo,
                    });
            }
        );

        const userConfigurationSelectedCalendars = getFilteredUserConfiguration()
            .ViewStateConfiguration.SelectedCalendarsTNarrow;

        userConfigurationSelectedCalendars?.forEach(calendarId => {
            resultCalendars.forEach(item => {
                if (calendarId === item.calculatedFolderId || calendarId === item.id.id) {
                    item.isSelected = true;
                }
            });
        });

        // sort the resultant calendars
        resultCalendars.sort((cal1, cal2) => cal1.displayName.localeCompare(cal2.displayName));
        if (groupHasDefaultCalendar) {
            resultCalendars = resultCalendars.filter(calendar => calendar !== defaultCalendar);
            resultCalendars.unshift(defaultCalendar);
        }
        sortedResultCalendars = sortedResultCalendars.concat(resultCalendars);
    });

    return {
        defaultCalendar: defaultCalendar,
        groups: resultGroups,
        calendars: sortedResultCalendars,
        folderIdMapping: folderIdMapping,
    };
}

export const isWritableCalendar = (
    calendarFolder: CalendarFolder,
    isCalendarReadOnly: boolean
): boolean => !isCalendarReadOnly && !!calendarFolder?.EffectiveRights?.CreateContents;

const isLocalCalendar = (calendarEntry: CalendarEntry & LocalCalendarEntry) =>
    !calendarEntry.IsGroupMailboxCalendar && calendarEntry.CalendarFolderId != undefined;
